/**
 * The HUD (GDD §7): built once per layout and updated every frame from a preallocated input, writing to the DOM
 * only when a value changes. Strings that change every frame come from precomputed tables, so a frame creates
 * nothing (GDD §10.5). Layout, scale and safe areas follow src/hud/layout.ts.
 */
import type { Announcer } from '../rules/announcer.ts';
import type { Settings } from '../storage/storage.ts';
import type { TouchElements } from '../input/touch.ts';
import { ABILITIES } from './chips.ts';
import { bannerText, type BannerText } from './banners.ts';
import { add, el } from './dom.ts';
import { formatScore, fuelPercent, OPACITY, pad2, PERCENT, roman, SCALE_STEPS, SCALE_X } from './format.ts';
import { buildLandscape } from './landscape.ts';
import { controlEdges, planLayout, type Frame, type HudLayout, type Insets, type LayoutPlan } from './layout.ts';
import { buildPauseMenu, type PauseAction } from './pauseMenu.ts';
import { setButtonKind, type HudRefs } from './parts.ts';
import { buildPortrait } from './portrait.ts';
import { buildSettings, type SettingsPanel } from './settings.ts';
import { config } from '../core/config.ts';

export type FlowState = 'loading' | 'title' | 'play' | 'over';

/** What the HUD shows this frame (filled in place by the flow; never reallocated). */
export interface HudInput {
  flow: FlowState;
  paused: boolean;
  score: number;
  hi: number;
  fuel: number;
  lives: number;
  level: number;
  /** The bridge guns are tracking the jet (D18). */
  engaged: boolean;
  /** 0..1 from the last bridge (or run start) to the next (D19). */
  progress: number;
  /** Title opacity while it fades after the start, 1..0 (GDD §7.1). */
  titleFade: number;
  banner: Announcer;
  /** GAME OVER's final and high score (§7.1). */
  final: number;
  finalHigh: number;
  newHigh: boolean;
  autoFire: boolean;
  leftHanded: boolean;
  /** Show the enemy-ability chips (on touch screens only for a few seconds after a level starts). */
  showAbilities: boolean;
}

export function createHudInput(banner: Announcer): HudInput {
  return {
    flow: 'loading',
    paused: false,
    score: 0,
    hi: 0,
    fuel: 1,
    lives: 0,
    level: 1,
    engaged: false,
    progress: 0,
    titleFade: 1,
    banner,
    final: 0,
    finalHigh: 0,
    newHigh: false,
    autoFire: false,
    leftHanded: false,
    showAbilities: true,
  };
}

export type MenuKind = 'pause' | 'settings';

export interface HudEvents {
  /** The pause (or, on the title screen, settings) button. */
  pauseButton(): void;
  settingsButton(): void;
  /** A tap or click anywhere on the title screen. */
  tapStart(): void;
  pauseMenu(a: PauseAction): void;
  settingsBack(): void;
  settingsChange(s: Settings): void;
  /** The touch controls were rebuilt (new layout). */
  rebind(el: TouchElements | null): void;
}

export interface Hud {
  readonly layout: HudLayout;
  readonly plan: LayoutPlan;
  readonly root: HTMLElement;
  readonly menus: HTMLElement;
  /** Whether touch controls are in use (judgment call 32). */
  touch: boolean;
  resize(w: number, h: number): void;
  update(s: HudInput): void;
  /** The red crash flash (GDD §7.1). */
  crash(): void;
  menu: MenuKind | null;
  openMenu(kind: MenuKind | null): void;
  moveFocus(dir: 1 | -1): void;
  confirm(): void;
  syncSettings(s: Settings): void;
  dispose(): void;
}

export interface HudOptions {
  doc?: Document;
  width: number;
  height: number;
  touch: boolean;
  settings: Settings;
  hapticsSupported: boolean;
  events: HudEvents;
  /** Safe-area insets (default: measured from env(safe-area-inset-*)). */
  insets?: () => Insets;
}

/** Reads env(safe-area-inset-*) through a hidden probe. */
function insetProbe(doc: Document): () => Insets {
  const p = el(doc, 'div');
  p.style.cssText =
    'position:fixed;left:0;top:0;width:0;height:0;visibility:hidden;pointer-events:none;' +
    'padding:env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)';
  doc.body.appendChild(p);
  return () => {
    const cs = getComputedStyle(p);
    return {
      top: parseFloat(cs.paddingTop) || 0,
      right: parseFloat(cs.paddingRight) || 0,
      bottom: parseFloat(cs.paddingBottom) || 0,
      left: parseFloat(cs.paddingLeft) || 0,
    };
  };
}

function placeFrame(outer: HTMLElement, inner: HTMLElement, f: Frame): void {
  outer.style.left = `${f.left}px`;
  outer.style.top = `${f.top}px`;
  outer.style.width = `${f.width}px`;
  outer.style.height = `${f.height}px`;
  inner.style.width = `${f.width / f.zoom}px`;
  inner.style.height = `${f.height / f.zoom}px`;
  inner.style.zoom = f.zoom === 1 ? '' : String(f.zoom);
}

/** Restarts the CSS animations under an element (a new banner, the title's return). */
function restart(e: HTMLElement, cls: string): void {
  e.classList.remove(cls);
  void e.offsetWidth;
  e.classList.add(cls);
}

export function createHud(o: HudOptions): Hud {
  const doc = o.doc ?? document;
  const insets = o.insets ?? insetProbe(doc);
  const root = el(doc, 'div', 'hw-hud');
  root.hidden = true;
  const shade = add(root, el(doc, 'div', 'hw-overlay'));
  const pOuter = add(root, el(doc, 'div', 'hw-frame'));
  const pInner = add(pOuter, el(doc, 'div', 'hw-zoom'));
  const cOuter = add(root, el(doc, 'div', 'hw-frame'));
  const cInner = add(cOuter, el(doc, 'div', 'hw-zoom'));
  const crashFx = add(root, el(doc, 'div', 'hw-overlay hw-crash'));
  // A discreet developer credit, always on screen (Mehdi, 2026-10-10).
  add(root, el(doc, 'div', 'hw-footer', 'Developed by Mehdi Kaveh'));
  doc.body.appendChild(root);

  // Menus: dimmed scene and a panel at the controls' scale (never under 1×, so targets stay ≥ 44 px).
  const menus = el(doc, 'div', 'hw-menus');
  menus.hidden = true;
  add(menus, el(doc, 'div', 'hw-dim'));
  const mOuter = add(menus, el(doc, 'div', 'hw-frame'));
  const mInner = add(mOuter, el(doc, 'div', 'hw-zoom'));
  const pause = buildPauseMenu(doc, (a) => o.events.pauseMenu(a));
  const settings: SettingsPanel = buildSettings(
    doc,
    o.settings,
    (s) => o.events.settingsChange(s),
    () => o.events.settingsBack(),
    o.hapticsSupported,
  );
  add(mInner, pause.el, settings.el);
  doc.body.appendChild(menus);

  let refs: HudRefs = buildLandscape(doc, false);
  let plan: LayoutPlan = planLayout(o.width, o.height, insets(), o.touch);
  let size = { w: o.width, h: o.height };
  const text: BannerText = { head: '', sub: '' };
  // Last values written (−1 / null: write on the next update).
  const last = {
    flow: '' as FlowState | '',
    paused: null as boolean | null,
    score: -1,
    hi: -1,
    pct: -1,
    low: null as boolean | null,
    lives: -1,
    level: -1,
    prog: -1,
    engaged: null as boolean | null,
    bannerId: -1,
    bannerShown: false,
    bannerOut: false,
    title: -1,
    autoFire: null as boolean | null,
    left: null as boolean | null,
    abilities: null as boolean | null,
  };
  const resetLast = (): void => {
    last.flow = '';
    last.paused = last.low = last.engaged = last.autoFire = last.left = last.abilities = null;
    last.score = last.hi = last.pct = last.lives = last.level = last.prog = last.title = -1;
    last.bannerId = -1;
    last.bannerShown = last.bannerOut = false;
  };

  function build(): void {
    refs = plan.layout === 'portrait' ? buildPortrait(doc) : buildLandscape(doc, plan.layout === 'touch');
    pInner.replaceChildren(...refs.panels);
    cInner.replaceChildren(...refs.controls);
    root.dataset.layout = plan.layout;
    root.classList.toggle('hw-portrait', plan.layout === 'portrait');
    shade.className = `hw-overlay ${plan.layout === 'portrait' ? 'hw-shade' : 'hw-vignette'}`;
    const btn = (b: HTMLButtonElement | null, f: () => void): void => {
      b?.addEventListener('click', (e) => {
        e.stopPropagation();
        b.blur();
        f();
      });
      b?.addEventListener('pointerdown', (e) => e.stopPropagation());
    };
    btn(refs.menuBtn, () =>
      refs.menuBtn?.dataset.kind === 'settings' ? o.events.settingsButton() : o.events.pauseButton(),
    );
    btn(refs.pauseBtn, () => o.events.pauseButton());
    btn(refs.settingsBtn, () => o.events.settingsButton());
    for (const c of [refs.pad, refs.fire]) c?.addEventListener('pointerdown', (e) => e.stopPropagation());
    resetLast();
    o.events.rebind(
      refs.pad && refs.thumb && refs.fireBtn ? { pad: refs.pad, thumb: refs.thumb, fire: refs.fireBtn } : null,
    );
  }

  function place(): void {
    placeFrame(pOuter, pInner, plan.panels);
    placeFrame(cOuter, cInner, plan.controls);
    placeFrame(mOuter, mInner, plan.controls);
    cInner.style.setProperty('--hw-edge', String(controlEdges(plan.controls.width / plan.controls.zoom)));
    // Buttons in the panels frame keep 44 px on screen when the panels shrink (§7.2).
    pInner.style.setProperty('--hw-min', String(Math.max(1, 1 / plan.panels.zoom)));
    // Touch landscape: banners fit between the steering pad and the fire button.
    if (plan.layout === 'touch') {
      const side = ((16 + 196 + 14) * plan.controls.zoom) / plan.panels.zoom;
      pInner.style.setProperty('--hw-banner-max', `calc(100% - ${2 * side}px)`);
    } else if (plan.layout === 'portrait') pInner.style.setProperty('--hw-banner-max', 'calc(100% - 32px)');
    else pInner.style.removeProperty('--hw-banner-max');
  }

  // Title screen: a tap or click anywhere starts (GDD §7.1).
  root.addEventListener('pointerdown', (e) => {
    if (root.classList.contains('hw-tap') && e.isPrimary) o.events.tapStart();
  });

  function relayout(force: boolean): void {
    const next = planLayout(size.w, size.h, insets(), hud.touch);
    const rebuild = force || next.layout !== plan.layout;
    plan = next;
    if (rebuild) build();
    place();
  }

  let touch = o.touch;
  const hud: Hud = {
    get layout() {
      return plan.layout;
    },
    get plan() {
      return plan;
    },
    root,
    menus,
    get touch() {
      return touch;
    },
    set touch(on: boolean) {
      if (on === touch) return;
      touch = on;
      relayout(false);
      last.flow = ''; // the controls' visibility follows touch
    },
    menu: null,
    resize(w, h) {
      size = { w, h };
      relayout(false);
    },
    update(s) {
      const r = refs;
      if (s.flow !== last.flow) {
        const was = last.flow;
        last.flow = s.flow;
        root.hidden = s.flow === 'loading';
        const title = s.flow === 'title';
        root.classList.toggle('hw-tap', title);
        r.press.hidden = !title;
        r.credit.hidden = !title;
        if (title) {
          r.title.hidden = false;
          r.title.style.opacity = '';
          if (was !== '') restart(r.title, 'hw-anim');
          last.title = -1;
        }
        const controls = s.flow === 'play' && touch;
        for (const c of [r.pad, r.fire]) if (c) c.hidden = !controls;
        last.autoFire = null; // the auto-fire label follows the controls
        if (r.pauseBtn) r.pauseBtn.hidden = title;
        if (r.menuBtn) setButtonKind(doc, r.menuBtn, title ? 'settings' : 'pause');
        if (title) r.bannerWrap.hidden = true;
      }
      if (s.paused !== last.paused) {
        last.paused = s.paused;
        root.classList.toggle('hw-paused', s.paused);
      }
      if (s.score !== last.score) {
        last.score = s.score;
        r.score.textContent = formatScore(s.score);
      }
      if (r.high && s.hi !== last.hi) {
        last.hi = s.hi;
        r.high.textContent = `HIGH · ${formatScore(s.hi)}`;
      }
      const pct = fuelPercent(s.fuel);
      if (pct !== last.pct) {
        last.pct = pct;
        const p = PERCENT[pct] ?? '0%';
        r.fuelPct.textContent = p;
        r.fuelFill.style.width = p;
      }
      const low = s.fuel < config.fuel.lowThreshold;
      if (low !== last.low) {
        last.low = low;
        r.fuelBox.classList.toggle('hw-is-low', low);
        r.lowFuel.hidden = !low;
        // Portrait: the LOW FUEL chip takes the section name's room, so the chip row never wraps.
        if (r.sectionYields) r.section.hidden = low;
      }
      const lives = Math.max(0, s.lives);
      if (lives !== last.lives) {
        last.lives = lives;
        for (let i = 0; i < r.jetIcons.length; i++) {
          const ic = r.jetIcons[i];
          if (ic) ic.style.display = i < lives ? '' : 'none';
        }
        if (r.jetsText) r.jetsText.textContent = `JETS ${roman(lives)}`;
      }
      if (s.level !== last.level) {
        last.level = s.level;
        const L = s.level;
        r.level.textContent = `LEVEL ${pad2(L)}`;
        const names = config.text.sections;
        r.section.textContent = names[(((L - 1) % names.length) + names.length) % names.length] ?? '';
        if (r.bridgeFrom) r.bridgeFrom.textContent = `BRIDGE ${pad2(L)}`;
        if (r.bridgeTo) r.bridgeTo.textContent = `BRIDGE ${pad2(L + 1)}`;
        let any = false;
        for (let i = 0; i < ABILITIES.length; i++) {
          const on = L >= (ABILITIES[i]?.from ?? Infinity);
          const c = r.abilityChips[i];
          if (c) c.hidden = !on;
          any = any || on;
        }
        r.abilities.dataset.any = any ? '1' : '';
        last.abilities = null;
      }
      const abil = s.showAbilities && r.abilities.dataset.any === '1';
      if (abil !== last.abilities) {
        last.abilities = abil;
        r.abilities.hidden = !abil;
      }
      const pv = s.progress;
      const prog = Math.round((pv < 0 ? 0 : pv > 1 ? 1 : pv) * SCALE_STEPS);
      if (prog !== last.prog) {
        last.prog = prog;
        r.progress.style.transform = SCALE_X[prog] ?? '';
      }
      const engaged = s.engaged && s.flow === 'play';
      if (engaged !== last.engaged) {
        last.engaged = engaged;
        r.alert.hidden = !engaged;
      }
      if (s.flow !== 'title') {
        const tf = s.titleFade;
        const op = Math.round((tf < 0 ? 0 : tf > 1 ? 1 : tf) * 100);
        if (op !== last.title) {
          last.title = op;
          r.title.hidden = op === 0;
          r.title.style.opacity = OPACITY[op] ?? '';
        }
      }
      if (s.autoFire !== last.autoFire) {
        last.autoFire = s.autoFire;
        // Shown only while auto-fire is on (it is off by default).
        if (r.autoFire) r.autoFire.hidden = !(s.autoFire && s.flow === 'play' && touch);
      }
      if (s.leftHanded !== last.left) {
        last.left = s.leftHanded;
        root.classList.toggle('hw-left', s.leftHanded);
      }
      // Banners (§7.3): hidden on the title screen.
      const b = s.banner;
      const shown = b.showing && s.flow !== 'title';
      if (shown && b.id !== last.bannerId) {
        last.bannerId = b.id;
        last.bannerOut = false;
        bannerText(b.current, text);
        const over = b.current.key === 'gameOver';
        r.banner.className = `hw-banner hw-k-${b.current.kind}${over ? ' hw-k-over' : ''}`;
        r.bannerHead.textContent = text.head;
        r.bannerSub.textContent = text.sub;
        r.bannerSub.hidden = text.sub === '';
        r.bannerRows.hidden = !over;
        r.bannerNew.hidden = !(over && s.newHigh);
        if (over) {
          r.bannerFinal.textContent = formatScore(s.final);
          r.bannerHigh.textContent = formatScore(s.finalHigh);
        }
        r.bannerWrap.hidden = false;
        restart(r.banner, 'hw-in');
      }
      if (shown !== last.bannerShown) {
        last.bannerShown = shown;
        if (!shown) r.bannerWrap.hidden = true;
      }
      const out = shown && b.left < 0.3;
      if (out !== last.bannerOut) {
        last.bannerOut = out;
        if (out) {
          r.banner.classList.remove('hw-in');
          r.banner.classList.add('hw-out');
        } else r.banner.classList.remove('hw-out');
      }
    },
    crash() {
      restart(crashFx, 'hw-flash');
    },
    openMenu(kind) {
      hud.menu = kind;
      menus.hidden = kind === null;
      pause.el.hidden = kind !== 'pause';
      settings.el.hidden = kind !== 'settings';
      if (kind) {
        const first = kind === 'pause' ? pause.buttons[0] : settings.focusables[0];
        first?.focus({ preventScroll: true });
      }
    },
    moveFocus(dir) {
      const list: HTMLElement[] =
        hud.menu === 'pause' ? pause.buttons : hud.menu === 'settings' ? settings.focusables : [];
      if (!list.length) return;
      const i = list.indexOf(doc.activeElement as HTMLElement);
      const next = list[(i + dir + list.length) % list.length] ?? list[0];
      next?.focus({ preventScroll: true });
    },
    confirm() {
      const a = doc.activeElement as HTMLElement | null;
      if (a && menus.contains(a)) a.click();
      else hud.moveFocus(1);
    },
    syncSettings(s) {
      settings.sync(s);
    },
    dispose() {
      root.remove();
      menus.remove();
    },
  };
  relayout(true);
  return hud;
}
